import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import type { ReactNode } from 'react';
import StatCard, { type StatCardProps } from './StatCard';
import styles from './StatCard.module.scss';
export type StatCardGridColumns = 2 | 3 | 4;
export type StatCardGridLayout = 'default' | 'metric';
/** The metric cell's stat element (Phase 5 PR 5.3, O16). */
export type StatCardGridStatAs = 'h2' | 'h3';
/** The metric cell's description size (Phase 5 PR 5.3 fix round, M1):
 * `compact` 15px muted by default; `body` 17px and `lg` 18px, at 1.5 in the
 * surface's text colour, where the description carries compliance copy that
 * may not shrink or fade (Q13). */
export type StatCardGridDescriptionSize = 'compact' | 'body' | 'lg';
/** The metric cell's stat role (Phase 6): `display` the display-stat role
 * (clamp(84px, 8.5vw, 150px)), `compact` the section role (clamp(44px, 6vw,
 * 76px), its phone steps), for four stats to a row. */
export type StatCardGridStatSize = 'display' | 'compact';

/** One cell of the metric grid. */
export interface StatCardMetricItem {
  stat: string;
  description: string;
  /** A micro label above the stat (Lekton 10px, uppercase). */
  label?: string;
  /** Optional fine-print below the description (e.g. a data source). */
  footnote?: ReactNode;
}

/** The default layout: a StatCard per item, in 2, 3 or 4 columns (4 by
 * default). */
export interface StatCardGridCardsProps {
  layout?: 'default';
  columns?: StatCardGridColumns;
  items: StatCardProps[];
}

/**
 * `metric` (incubating): the redesign's hairline metric grid (delta §3 row
 * 31). Cells on the surface they sit on with 1px rules between them, at least
 * 310px tall, an optional label above the stat, the stat on the `display-stat`
 * role and the description pinned to the bottom. 2 or 3 columns (3 by
 * default): the stat role needs about a third of the container; 4 (Phase 6,
 * the Home stats) with `statSize="compact"`, the stat a step down.
 */
export type StatCardGridMetricProps = StatCardGridMetricBaseProps & ({
  columns?: Exclude<StatCardGridColumns, 4>;
  statSize?: StatCardGridStatSize;
} | {
  columns: 4;
  statSize: 'compact';
});
interface StatCardGridMetricBaseProps {
  layout: 'metric';
  items: StatCardMetricItem[];
  /** The stat's heading level, `h3` by default; `h2` where a page's stats
   * are section-level headings today (`/info-overview`). The look is the
   * same at either level. */
  statAs?: StatCardGridStatAs;
  /** The description's size, `compact` (15px, muted) by default. `body`
   * (17px) and `lg` (18px) set it at 1.5 in the surface's text colour: the
   * paid landers' affordability stats carry the financial-aid qualifier there
   * at the size and colour they had before Helios (Q13: no smaller type). */
  descriptionSize?: StatCardGridDescriptionSize;
}
export type StatCardGridProps = StatCardGridCardsProps | StatCardGridMetricProps;
function StatCardGrid(props) {
  const $ = _c(22);
  const columns = props.columns ?? (props.layout === "metric" ? 3 : 4);
  const t0 = styles[`helios-stat-grid--cols-${columns}`];
  const t1 = props.layout === "metric" && styles["helios-stat-grid--metric"];
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = classNames(styles["helios-stat-grid"], t0, t1);
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const className = t2;
  if (props.layout === "metric") {
    const Stat = props.statAs ?? "h3";
    const statSize = props.statSize ?? "display";
    const descriptionSize = props.descriptionSize ?? "compact";
    const t3 = descriptionSize !== "compact" && styles[`helios-stat-metric__desc--${descriptionSize}`];
    let t4;
    if ($[3] !== t3) {
      t4 = classNames(styles["helios-stat-metric__desc"], t3);
      $[3] = t3;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    const descriptionClassName = t4;
    let t5;
    if ($[5] !== Stat || $[6] !== descriptionClassName || $[7] !== props.items || $[8] !== statSize) {
      let t6;
      if ($[10] !== Stat || $[11] !== descriptionClassName || $[12] !== statSize) {
        t6 = (item, index) => <div key={`${index}-${item.stat}`} className={styles["helios-stat-metric"]}>{item.label && <p className={styles["helios-stat-metric__label"]}>{item.label}</p>}<Stat className={classNames(styles["helios-stat-metric__stat"], statSize !== "display" && styles[`helios-stat-metric__stat--${statSize}`])}>{item.stat}</Stat><p className={descriptionClassName}>{item.description}</p>{item.footnote && <p className={styles["helios-stat-metric__footnote"]}><small>{item.footnote}</small></p>}</div>;
        $[10] = Stat;
        $[11] = descriptionClassName;
        $[12] = statSize;
        $[13] = t6;
      } else {
        t6 = $[13];
      }
      t5 = props.items.map(t6);
      $[5] = Stat;
      $[6] = descriptionClassName;
      $[7] = props.items;
      $[8] = statSize;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[14] !== className || $[15] !== t5) {
      t6 = <div className={className}>{t5}</div>;
      $[14] = className;
      $[15] = t5;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    return t6;
  }
  let t3;
  if ($[17] !== props.items) {
    t3 = props.items.map(_temp);
    $[17] = props.items;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== className || $[20] !== t3) {
    t4 = <div className={className}>{t3}</div>;
    $[19] = className;
    $[20] = t3;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return t4;
}
function _temp(item_0, index_0) {
  return <StatCard key={`${index_0}-${item_0.stat}`} {...item_0} />;
}
export default StatCardGrid;